import { MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { useLanguage, translations } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { SellCarDialog } from "./SellCarDialog";
import { Link } from "react-router-dom";

export function Footer() {
  const [feedback, setFeedback] = useState("");
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error(t.enterFeedback);
      return;
    }

    setIsSubmitting(true);
    try {
      const anonymousUserId = localStorage.getItem('ts_anonymous_user_id') || undefined;
      const device = navigator.userAgent;

      const { data, error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          feedback: feedback.trim(),
          anonymous_user_id: anonymousUserId,
          device,
        },
      });

      if (error) {
        console.error('Feedback submission error:', error);
        toast.error(language === 'th' ? 'เกิดข้อผิดพลาด กรุณาลองใหม่' : 'Something went wrong. Please try again.');
        return;
      }

      toast.success(t.thankYouFeedback);
      setFeedback("");
      setOpen(false);
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error(language === 'th' ? 'เกิดข้อผิดพลาด กรุณาลองใหม่' : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-footer-light dark:bg-gradient-footer relative mt-auto">
      {/* Top gradient border */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="container mx-auto px-4 py-8">
        {/* SEO Internal Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 text-sm">
          <div>
            <h3 className="font-semibold mb-3 text-foreground">
              {language === 'th' ? 'ยี่ห้อยอดนิยม' : 'Popular Brands'}
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/?brand=bmw" className="hover:text-foreground transition-colors">BMW มือสอง</Link></li>
              <li><Link to="/?brand=mercedes" className="hover:text-foreground transition-colors">Mercedes มือสอง</Link></li>
              <li><Link to="/?brand=porsche" className="hover:text-foreground transition-colors">Porsche มือสอง</Link></li>
              <li><Link to="/?brand=audi" className="hover:text-foreground transition-colors">Audi มือสอง</Link></li>
              <li><Link to="/?brand=volvo" className="hover:text-foreground transition-colors">Volvo มือสอง</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-foreground">
              {language === 'th' ? 'ช่วงราคา' : 'Price Range'}
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/?priceRange=800k-1m" className="hover:text-foreground transition-colors">฿800,000 - 1,000,000</Link></li>
              <li><Link to="/?priceRange=1m-1.5m" className="hover:text-foreground transition-colors">฿1,000,000 - 1,500,000</Link></li>
              <li><Link to="/?priceRange=1.5m-2m" className="hover:text-foreground transition-colors">฿1,500,000 - 2,000,000</Link></li>
              <li><Link to="/?priceRange=2m-2.5m" className="hover:text-foreground transition-colors">฿2,000,000 - 2,500,000</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-foreground">
              {language === 'th' ? 'พื้นที่' : 'Locations'}
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/?location=bangkok" className="hover:text-foreground transition-colors">รถมือสอง กรุงเทพ</Link></li>
              <li><Link to="/?location=nakhon-pathom" className="hover:text-foreground transition-colors">รถมือสอง นครปฐม</Link></li>
              <li><Link to="/?location=ratchaphruek" className="hover:text-foreground transition-colors">รถมือสอง ราชพฤกษ์</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-foreground">
              {language === 'th' ? 'เกี่ยวกับเรา' : 'About'}
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-1">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <span>MotorVerified</span>
              </li>
              <li>รถมือสองคุณภาพ</li>
              <li>ไมล์แท้ เซอร์วิสครบ</li>
            </ul>
          </div>
        </div>

        {/* Mobile: stacked layout, Desktop: single row */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full border-t border-border/50 pt-6">
          {/* Branding - left on desktop */}
          <p className="text-base text-muted-foreground text-center md:text-left">
            TransShift — <span className="font-medium text-foreground">{t.earlyEditionProgram}</span>
          </p>
          
          {/* Buttons - center on desktop */}
          <div className="flex items-center justify-center gap-3">
            <SellCarDialog />

            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open('https://lin.ee/o4EY8bL', '_blank')}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              {t.lineCTA}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {t.feedbackCTA}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t.sendFeedback}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <Textarea 
                    placeholder={t.shareFeedback}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    rows={5}
                    className="resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
                      {t.cancel}
                    </Button>
                    <Button onClick={handleSubmitFeedback} disabled={isSubmitting}>
                      {isSubmitting ? (language === 'th' ? 'กำลังส่ง...' : 'Sending...') : t.submit}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* MotorVerified - right on desktop */}
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            <p className="text-base font-medium">
              {t.motorVerifiedBy} <span className="text-primary">{t.transShift}</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
